import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams , Link } from "react-router-dom";
import { ArrowRight, Check, Clock, X } from "lucide-react";
import { TopBar } from "../components/TopBar";
import { AppShell, AppFooter } from "../components/Shell";
import { StatBar } from "../components/StatBar";
import { ReportProfile } from "../components/ReportProfile";
import { api, errorMessage, imgSrc } from "../lib/api";
import { STATIONS, WEIGHTS, formatTime } from "../constants/rox";
import { ResultsList } from "../components/Results";
import { useT, useLabels } from "../i18n";

export default function PairDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, fmtDate } = useT();
  const L = useLabels();
  const [pair, setPair] = useState(null);
  const [interest, setInterest] = useState(null);
  const [error, setError] = useState("");
  const timer = useRef(null);

  useEffect(() => {
    api.get(`/pairs/${id}`).then((r) => {
      setPair(r.data);
      if (r.data.interest_id) setInterest({ id: r.data.interest_id, status: r.data.interest_status, match_id: r.data.match_id });
    }).catch((e) => setError(errorMessage(e)));
    return () => clearTimeout(timer.current);
  }, [id]);

  useEffect(() => {
    if (!interest || interest.status !== "pending") return;
    timer.current = setTimeout(async () => {
      const { data } = await api.get(`/interests/${interest.id}`);
      setInterest(data);
      if (data.status === "matched") navigate(`/match/${data.match_id}`);
    }, 900);
    return () => clearTimeout(timer.current);
  }, [interest, navigate]);

  const express = async () => {
    setError("");
    try {
      const { data } = await api.post("/interests", { profile_id: id });
      setInterest(data);
      if (data.status === "matched") navigate(`/match/${data.match_id}`);
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  const withdraw = async () => {
    setError("");
    try {
      await api.delete(`/interests/${interest.id}`);
      setInterest(null);
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  if (error && !pair) return <AppShell><TopBar onBack={() => navigate("/recherche")} /><main className="px-5 pt-6 lg:px-8" data-testid="pair-not-found"><p className="text-rox-orange">{error}</p><Link to="/recherche" data-testid="pair-not-found-back" className="btn-primary mt-6 lg:max-w-sm">{t("search_btn")}</Link></main></AppShell>;
  if (!pair) return <AppShell><TopBar /><p className="p-5 text-sm text-rox-muted">{t("loading")}</p></AppShell>;

  const status = interest?.status;
  return (
    <AppShell>
      <TopBar onBack={() => navigate("/recherche")} />
      <main className="pb-36 lg:grid lg:grid-cols-[420px_1fr] lg:gap-10 lg:px-8 lg:pt-6" data-testid="pair-detail-page">
        <div className="relative aspect-[4/3] overflow-hidden bg-rox-gray lg:aspect-[3/4] lg:rounded-[6px] lg:self-start">
          {pair.photo_url ? (
            <img src={imgSrc(pair.photo_url)} alt={pair.first_name} className="h-full w-full object-cover" />
          ) : (
            <span className="flex h-full w-full items-center justify-center font-display text-8xl uppercase text-rox-off/30">{pair.first_name.slice(0, 2)}</span>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-rox-black via-rox-black/20 to-transparent" />
          <span data-testid="detail-score" className="absolute left-5 top-4 bg-rox-orange px-3 py-1.5 font-display text-base uppercase tracking-wide text-rox-black">{pair.score}% {t("compatible")}</span>
          <div className="absolute bottom-4 left-5 right-5">
            <p className="eyebrow text-rox-orange">{t("profile_eyebrow")}</p>
            <h1 className="h-display text-5xl" data-testid="detail-name">{pair.first_name}{pair.age ? `, ${t("age_years", pair.age)}` : ""}</h1>
            <p className="text-sm text-rox-off/80">{pair.city}{pair.distance_km ? ` · ${pair.distance_km} km` : ""} · {L.level(pair)}</p>
          </div>
        </div>

        <section className="px-5 pt-5 stagger lg:px-0 lg:pt-0">
          <div className="grid grid-cols-2 gap-2">
            {[[t("race"), pair.race_name, pair.race_date ? fmtDate(pair.race_date) : ""], [t("format"), L.category(pair), ""], [t("level"), L.level(pair), ""], [t("chrono"), formatTime(pair.target_time), L.goal(pair)]].map(([k, v, sub]) => (
              <div key={k} className="panel p-3">
                <p className="eyebrow">{k}</p>
                <p className="mt-1 font-display text-base uppercase leading-tight">{v}</p>
                {sub && <p className="mt-0.5 text-[11px] text-rox-muted">{sub}</p>}
              </div>
            ))}
          </div>
          <p className="mt-4 text-sm"><span className="text-rox-orange">■</span> {L.strengths(pair)}</p>
          <p className="mt-1 text-xs text-rox-muted">{L.availability(pair).join(" · ")}</p>
          {pair.bio && <p className="mt-5 border-l-2 border-rox-orange pl-4 text-base text-rox-off/90" data-testid="detail-bio">« {pair.bio} »</p>}
          {pair.results?.length > 0 && (
            <>
              <h2 className="h-display mt-8 text-3xl">{t("previous_hyrox")}</h2>
              <p className="mb-3 mt-1 text-xs text-rox-muted">{t("results_sub")}</p>
              <ResultsList results={pair.results} />
            </>
          )}

          <h2 className="h-display mt-8 text-3xl">{t("why_match_a")} <span className="text-rox-orange">{t("why_match_b")}</span></h2>
          <ul className="mt-4 space-y-2" data-testid="match-reasons">
            {L.reasons(pair).map((r) => (
              <li key={r} className="flex items-start gap-3 text-sm"><Check size={16} className="mt-0.5 shrink-0 text-rox-orange" strokeWidth={3} /> {r}</li>
            ))}
          </ul>
          <div className="panel mt-5 space-y-2.5 p-4" data-testid="score-breakdown">
            {WEIGHTS.map((w, i) => <StatBar key={w.key} label={L.weight(w.key)} value={pair.breakdown[w.key]} max={w.max} delay={i * 70} />)}
          </div>

          <h2 className="h-display mt-8 text-3xl">{t("stations")}</h2>
          <p className="mt-1 text-xs text-rox-muted">{t("stations_hint", pair.first_name)}</p>
          <div className="mt-4 space-y-3">
            {STATIONS.map((s, i) => {
              const comp = pair.complementary_stations.includes(s.key);
              return (
                <div key={s.key} data-testid={`station-${s.key}`}>
                  <div className="flex items-center justify-between text-xs">
                    <span className={`eyebrow ${comp ? "text-rox-orange" : "text-rox-off/80"}`}>{L.station(s.key)}{comp ? " ·" : ""}</span>
                    <span className="font-display tracking-wide">{pair.scores[s.key]} <span className="text-rox-muted">/ {pair.me_scores[s.key]}</span></span>
                  </div>
                  <div className="mt-1.5 flex gap-1">
                    <div className="h-1.5 flex-1 overflow-hidden rounded-sm bg-rox-gray"><div className="h-full bg-rox-orange animate-fill-bar" style={{ width: `${pair.scores[s.key] * 10}%`, animationDelay: `${i * 50}ms` }} /></div>
                    <div className="h-1.5 flex-1 overflow-hidden rounded-sm bg-rox-gray"><div className="h-full bg-rox-off/50 animate-fill-bar" style={{ width: `${pair.me_scores[s.key] * 10}%`, animationDelay: `${i * 50}ms` }} /></div>
                  </div>
                </div>
              );
            })}
          </div>
          <ReportProfile profileId={id} />
        </section>
      </main>
      <AppFooter className="!pb-36 lg:!pb-32" />

      <div className="fixed inset-x-0 bottom-0 z-20 mx-auto w-full max-w-md bg-gradient-to-t from-rox-black via-rox-black/95 to-transparent px-5 pb-6 pt-8 lg:max-w-[1150px] lg:px-8">
        {error && <p className="mb-3 text-sm text-rox-orange" data-testid="interest-error">{error}</p>}
        {pair.matched || status === "matched" ? (
          <button data-testid="go-to-match" className="btn-primary" onClick={() => navigate(`/match/${pair.match_id || interest?.match_id}`)}>
            {t("its_match_contact")} <ArrowRight size={20} />
          </button>
        ) : status === "pending" ? (
          <div data-testid="interest-pending" className="btn-ghost cursor-default">
            <span className="h-2.5 w-2.5 rounded-full bg-rox-orange animate-pulse-dot" /> {t("interest_sent")}
          </div>
        ) : status === "no_reply" || status === "sent" ? (
          <div className="space-y-2">
            <div data-testid="interest-no-reply" className="btn-ghost cursor-default text-rox-off/70"><Clock size={18} /> {status === "sent" ? t("interest_waiting") : t("waiting_for", pair.first_name)}</div>
            <button data-testid="withdraw-interest" onClick={withdraw} className="flex w-full items-center justify-center gap-2 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-rox-muted transition-colors hover:text-rox-orange">
              <X size={14} /> {t("withdraw")}
            </button>
          </div>
        ) : (
          <button data-testid="interested-button" className="btn-primary" onClick={express}>
            {t("interested")} <ArrowRight size={20} />
          </button>
        )}
      </div>
    </AppShell>
  );
}
